import { satisfies } from "compare-versions";
import { cloneDeep, find, memoize, reverse } from "lodash";

export const FRAMEWORK_VERSION_CONSTRAINTS = ["^0.1.0", "^0.2.0", "^0.3.0", "^0.3.1", "^1.0.0"] as const;
export type FrameworkVersionConstraint = (typeof FRAMEWORK_VERSION_CONSTRAINTS)[number];

export interface FrameworkVersion {
  constraint: FrameworkVersionConstraint;
  name: string;
  released: boolean;
  description: string;
  nodeVersion: "16" | "18" | "20";
  fastifyVersion: "3" | "4";
}

export const AvailableVersions: FrameworkVersion[] = [
  {
    constraint: "^0.1.0",
    name: "v0.1",
    released: true,
    description: "Node 16.x, Fastify 3.x",
    nodeVersion: "16",
    fastifyVersion: "3",
  },
  {
    constraint: "^0.2.0",
    name: "v0.2",
    released: true,
    description: "Node 18.x, Fastify 3.x",
    nodeVersion: "18",
    fastifyVersion: "3",
  },
  {
    constraint: "^0.3.0",
    name: "v0.3",
    released: true,
    description: "Node 20.x, Fastify 4.x",
    nodeVersion: "20",
    fastifyVersion: "4",
  },
  {
    constraint: "^0.3.1",
    name: "v0.3.1",
    released: true,
    description: "Node 20.x, Fastify 4.x, support for Shopify webhook disabling",
    nodeVersion: "20",
    fastifyVersion: "4",
  },
  {
    constraint: "^1.0.0",
    name: "v1.0.0",
    released: true,
    description: "Node 20.x, Fastify 4.x, support for source control and multi-environment",
    nodeVersion: "20",
    fastifyVersion: "4",
  },
];

export const latestVersion = memoize(
  () => reverse(cloneDeep(AvailableVersions)).find((version) => version.name != "edge" && version.released)!
);
export const edgeVersion = memoize(() => AvailableVersions.at(-1)!);

export const AvailableVersionNames = AvailableVersions.map((version) => version.name);

export const latestConstraint = memoize(() => latestVersion().constraint);
export const edgeConstraint = memoize(() => edgeVersion().constraint);

export const labelForConstraint = (constraint: string) =>
  find(AvailableVersions, { constraint: constraint as FrameworkVersionConstraint })?.name ?? constraint;

export const doesVersionSupportSourceControl = (constraint: string) => {
  return satisfies(constraint, ">=1.0.0");
};
export const constraintForLabel = (label: string) => find(AvailableVersions, (item) => item.name === label)?.constraint;
